import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button, Spinner } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Badge, ErrorBox, Section } from '@/components/ui/misc';
import { Table, TBody, TD, TR } from '@/components/ui/table';
import { auth } from '@/lib/auth';
import { downloadBackupCodes } from '@/lib/backup-codes';
import { relTime } from '@/lib/format';
import { qk, useMe } from '@/lib/queries';
import { IS_MOCK } from '@/lib/utils';
import { QrCanvas } from '@/routes/setup';

export function TotpDialog({ mode, onClose }: { mode: 'enable' | 'disable'; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useMe();
  const [password, setPassword] = useState('');
  const [enrolled, setEnrolled] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const done = async (msg: string) => {
    toast.success(msg);
    await qc.invalidateQueries({ queryKey: qk.me });
    await qc.invalidateQueries({ queryKey: qk.session });
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    (async () => {
      if (mode === 'disable') {
        await auth.disableTotp(password);
        await done('Two-factor disabled');
      } else if (!enrolled) {
        setEnrolled(await auth.enableTotp(password));
      } else {
        await auth.verifyTotp({ code: code.trim() });
        await done('Two-factor enabled');
      }
    })()
      .catch(setError)
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={mode === 'enable' ? 'Enable TOTP' : 'Disable TOTP'}>
        <form onSubmit={submit} className="flex flex-col gap-3">
          {!enrolled ? (
            <Field label="Confirm your password" htmlFor="sec-pw">
              <Input
                id="sec-pw"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          ) : (
            <>
              <div className="flex justify-center">
                <QrCanvas value={enrolled.totpURI} />
              </div>
              <Field label="6-digit code" htmlFor="sec-code">
                <Input
                  id="sec-code"
                  prompt
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={IS_MOCK ? '123456' : ''}
                />
              </Field>
              <div className="rounded-sm border border-border p-2 text-sm tabular">
                <p className="mb-1 text-xs text-warn">Backup codes (each works once):</p>
                <div className="grid grid-cols-2">
                  {enrolled.backupCodes.map((c) => (
                    <span key={c}>{c}</span>
                  ))}
                </div>
                <div className="mt-2 flex gap-2 border-t border-border pt-2">
                  <Button
                    size="sm"
                    onClick={() => downloadBackupCodes(enrolled.backupCodes, me.data?.user.email ?? '')}
                  >
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void navigator.clipboard
                        ?.writeText(enrolled.backupCodes.join('\n'))
                        .then(() => toast.success('Backup codes copied'))
                    }
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </>
          )}
          <ErrorBox error={error} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={mode === 'disable' ? 'danger' : 'primary'} loading={busy}>
              {mode === 'disable' ? 'Disable' : enrolled ? 'Verify' : 'Continue'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SecuritySection() {
  const qc = useQueryClient();
  const me = useMe();
  const passkeys = useQuery({ queryKey: qk.passkeys, queryFn: () => auth.listPasskeys() });
  const sessions = useQuery({ queryKey: qk.sessions, queryFn: () => auth.listSessions() });
  const [totp, setTotp] = useState<'enable' | 'disable' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const run = async (key: string, fn: () => Promise<void>, ok: string, keys: QueryKey[]) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      for (const k of keys) await qc.invalidateQueries({ queryKey: k });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const enabled = me.data?.user.twoFactorEnabled;
  return (
    <div className="flex flex-col gap-3">
      <Section
        title="Two-factor authentication (TOTP)"
        actions={
          enabled ? (
            <Button size="sm" variant="danger" onClick={() => setTotp('disable')}>
              Disable
            </Button>
          ) : (
            <Button size="sm" variant="primary" onClick={() => setTotp('enable')}>
              Enable
            </Button>
          )
        }
      >
        <p className="text-sm">
          Status: {enabled ? <Badge tone="success">enabled</Badge> : <Badge tone="warn">not enabled</Badge>}
          {me.data?.needs2fa ? (
            <span className="ml-2 text-warn">Required for your role ({me.data.role}).</span>
          ) : null}
        </p>
      </Section>
      <Section
        title="Passkeys"
        actions={
          <Button
            size="sm"
            loading={busy === 'add-pk'}
            onClick={() =>
              void run('add-pk', () => auth.addPasskey('This device'), 'Passkey added', [qk.passkeys, qk.me])
            }
          >
            + Add passkey
          </Button>
        }
        bodyClassName="p-0"
      >
        {passkeys.isPending ? (
          <Spinner />
        ) : (passkeys.data ?? []).length === 0 ? (
          <p className="p-3 text-sm text-muted">No passkeys registered.</p>
        ) : (
          <Table>
            <TBody>
              {(passkeys.data ?? []).map((p) => (
                <TR key={p.id}>
                  <TD>⚿ {p.name ?? 'Passkey'}</TD>
                  <TD className="text-muted">{p.deviceType ?? ''}</TD>
                  <TD className="text-muted">added {relTime(p.createdAt)}</TD>
                  <TD className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger"
                      loading={busy === p.id}
                      onClick={() =>
                        void run(p.id, () => auth.deletePasskey(p.id), 'Passkey removed', [
                          qk.passkeys,
                          qk.me,
                        ])
                      }
                    >
                      Remove
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Section>
      <Section title="Active sessions" bodyClassName="p-0">
        {sessions.isPending ? (
          <Spinner />
        ) : sessions.isError ? (
          <div className="flex flex-col items-start gap-2 p-3.5">
            <ErrorBox error={sessions.error} />
            <Button size="sm" onClick={() => void sessions.refetch()} loading={sessions.isFetching}>
              Try again
            </Button>
          </div>
        ) : (sessions.data ?? []).length === 0 ? (
          <p className="p-3.5 text-sm text-muted">No other sessions found.</p>
        ) : (
          <Table>
            <TBody>
              {(sessions.data ?? []).map((s) => (
                <TR key={s.id}>
                  <TD className="max-w-64 truncate" title={s.userAgent ?? ''}>
                    {s.userAgent ?? 'unknown device'}{' '}
                    {s.current ? <Badge tone="accent">this session</Badge> : null}
                  </TD>
                  <TD className="text-muted tabular">{s.ipAddress ?? '—'}</TD>
                  <TD className="text-muted">
                    signed in {relTime(s.createdAt)} · active {relTime(s.lastActiveAt)}
                  </TD>
                  <TD className="text-right">
                    {!s.current ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger"
                        loading={busy === s.id}
                        onClick={() =>
                          void run(s.id, () => auth.revokeSession(s.id), 'Session revoked', [qk.sessions])
                        }
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Section>
      {totp ? <TotpDialog mode={totp} onClose={() => setTotp(null)} /> : null}
    </div>
  );
}
